import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col, Button, Table, Form, ProgressBar, Badge, Alert, Card } from "react-bootstrap";
import { keyUserId, keyAccessToken } from "../../assets/js/SessionStorageNames";
import { GlobalContext } from "../../contexts/GlobalContext";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLLING_INTERVAL = 3000;
const STORAGE_KEY_PREFIX = "tagDeletedUnsold_processId";
const getStorageKey = (entId) => entId ? `${STORAGE_KEY_PREFIX}_${entId}` : null;

const getHeaders = () => ({
  "x-api-key": process.env.REACT_APP_API_KEY,
  "x-access-token": localStorage.getItem(keyAccessToken),
  "x-user-id": localStorage.getItem(keyUserId),
});

export default function TagDeletedUnsold() {
  const globalContext = useContext(GlobalContext);
  const entId = globalContext.currentEnterpriseId;

  // States
  const [view, setView] = useState("idle"); // idle | loading | preview | processing | completed | error
  const [limit, setLimit] = useState(10);
  const [products, setProducts] = useState([]);
  const [totalMlProducts, setTotalMlProducts] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [processMonitorId, setProcessMonitorId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [processData, setProcessData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [completedProcess, setCompletedProcess] = useState(null);

  const pollingRef = useRef(null);

  // Check for active process on mount and when enterprise changes
  useEffect(() => {
    if (entId) checkActiveProcess();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [entId]);

  const checkActiveProcess = async () => {
    try {
      const storageKey = getStorageKey(entId);
      if (!storageKey) return;
      const savedProcessId = localStorage.getItem(storageKey);

      // Check backend scoped to current enterprise
      const resp = await axios.get(`${BACKEND_URL}/v1/close-unsold/active-process`, {
        headers: getHeaders(),
        params: { entId },
      });

      const activeProcess = resp.data?.data?.process;

      if (activeProcess) {
        setProcessMonitorId(activeProcess._id);
        localStorage.setItem(storageKey, activeProcess._id);
        setView("processing");
        startPolling(activeProcess._id);
      } else if (savedProcessId) {
        // Check the saved process state
        try {
          const processResp = await axios.get(
            `${BACKEND_URL}/v1/process-monitor/${savedProcessId}`,
            { headers: getHeaders() }
          );
          const data = processResp.data?.data;
          if (data) {
            if (data.advance >= 100) {
              setCompletedProcess({ _id: savedProcessId, data: data.data });
              setProcessData(data.data);
              setProgress(data.advance || 100);
              setView("completed");
            }
          }
        } catch (e) {
          localStorage.removeItem(storageKey);
        }
      }
    } catch (err) {
      console.error("Error checking active process:", err);
    }
  };

  // Preview - fetch eligible products
  const handlePreview = async () => {
    if (!entId) {
      globalContext.showModalWithMsg("Debe seleccionar una empresa primero");
      return;
    }

    setView("loading");
    setErrorMsg(null);

    try {
      const resp = await axios.get(
        `${BACKEND_URL}/v1/close-unsold/preview/${entId}`,
        {
          headers: getHeaders(),
          params: { limit },
        }
      );

      const data = resp.data?.data;
      setProducts(data?.products || []);
      setTotalMlProducts(data?.totalMlProducts || 0);
      setSelectedProducts((data?.products || []).map((p) => p.mlId));
      setView("preview");
    } catch (err) {
      console.error("Preview error:", err);
      setErrorMsg(err.response?.data?.message || "Error al obtener vista previa");
      setView("error");
    }
  };

  // Select / deselect individual product
  const toggleProduct = (mlId) => {
    setSelectedProducts((prev) =>
      prev.includes(mlId) ? prev.filter((id) => id !== mlId) : [...prev, mlId]
    );
  };

  // Select / deselect all
  const toggleAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((p) => p.mlId));
    }
  };

  // Start the deletion process
  const handleStart = async () => {
    if (!selectedProducts.length) {
      globalContext.showModalWithMsg("Debe seleccionar al menos un producto");
      return;
    }

    try {
      setView("processing");
      setProgress(0);
      setProcessData(null);

      const resp = await axios.post(
        `${BACKEND_URL}/v1/close-unsold/start/${entId}`,
        { productIds: selectedProducts },
        { headers: getHeaders() }
      );

      const monitorId = resp.data?.data?.monitor;
      if (monitorId) {
        setProcessMonitorId(monitorId);
        const storageKey = getStorageKey(entId);
        if (storageKey) localStorage.setItem(storageKey, monitorId);
        startPolling(monitorId);
      }
    } catch (err) {
      console.error("Start error:", err);
      setErrorMsg(err.response?.data?.message || "Error al iniciar el proceso");
      setView("error");
    }
  };

  // Polling for process monitor
  const startPolling = useCallback((monitorId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/v1/process-monitor/${monitorId}`,
          { headers: getHeaders() }
        );

        const data = resp.data?.data;

        if (data) {
          setProgress(data.advance || 0);
          setProcessData(data.data || {});
          if (data.advance >= 100) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setCompletedProcess({ _id: monitorId, data: data.data });
            setView("completed");
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
        // If error contains a message, the process failed
        if (err.response?.data?.message) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setErrorMsg(err.response.data.message);
          setView("error");
        }
      }
    }, POLLING_INTERVAL);
  }, []);

  // Cancel process
  const handleCancel = async () => {
    if (!processMonitorId) return;

    try {
      await axios.put(
        `${BACKEND_URL}/v1/process-monitor/${processMonitorId}/desactivate`,
        {},
        { headers: getHeaders() }
      );

      if (pollingRef.current) clearInterval(pollingRef.current);
      const storageKey = getStorageKey(entId);
      if (storageKey) localStorage.removeItem(storageKey);
      globalContext.showModalWithMsg("Proceso cancelado exitosamente");
      setView("idle");
      setProcessMonitorId(null);
      setProgress(0);
      setProcessData(null);
    } catch (err) {
      console.error("Cancel error:", err);
      globalContext.showModalWithMsg(err.response?.data?.message || "Error al cancelar");
    }
  };


  // Reset to idle
  const handleReset = () => {
    const storageKey = getStorageKey(entId);
    if (storageKey) localStorage.removeItem(storageKey);
    setView("idle");
    setProducts([]);
    setSelectedProducts([]);
    setProcessMonitorId(null);
    setProgress(0);
    setProcessData(null);
    setCompletedProcess(null);
    setErrorMsg(null);
    if (pollingRef.current) clearInterval(pollingRef.current);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-VE", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-tag mr-2"></i>
                Marcar Productos Sin Ventas como Deleted
              </h4>
              <small className="text-muted">
                Marca productos con 0 ventas y más de 6 meses en ML como "deleted" en amazon_scrap_products
              </small>
            </div>
            <Link to="/admin/testing/admin">
              <Button variant="outline-secondary" size="sm">
                ← Volver a Testing Admin
              </Button>
            </Link>
          </div>
          <hr />
        </Col>
      </Row>

      {/* Enterprise check */}
      {!entId && (
        <Alert variant="warning">
          <strong>⚠️ Atención:</strong> Debe seleccionar una empresa antes de continuar.
        </Alert>
      )}

      {/* IDLE VIEW */}
      {view === "idle" && (
        <Card>
          <Card.Body>
            <Card.Title>Configuración</Card.Title>
            <Form>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>
                  Cantidad de productos a consultar:
                </Form.Label>
                <Col sm={3}>
                  <Form.Control
                    as="select"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                  >
                    {[1, 10, 50, 100, 500, 1000].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col sm={3}>
                  <Button
                    variant="primary"
                    onClick={handlePreview}
                    disabled={!entId}
                  >
                    🔍 Vista Previa
                  </Button>
                </Col>
              </Form.Group>
            </Form>

            <Alert variant="info" className="mt-3">
              <strong>ℹ️ Criterios de filtrado:</strong>
              <ul className="mb-0 mt-1">
                <li>Productos publicados en ML con estado <Badge variant="success">active</Badge></li>
                <li>Con <strong>0 ventas</strong> (sold_quantity = 0)</li>
                <li>Publicados hace más de <strong>6 meses</strong></li>
              </ul>
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* LOADING VIEW */}
      {view === "loading" && (
        <Card>
          <Card.Body className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
            <p>Consultando ML API y filtrando productos elegibles...</p>
            <small className="text-muted">Esto puede tomar unos momentos</small>
          </Card.Body>
        </Card>
      )}

      {/* PREVIEW VIEW */}
      {view === "preview" && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <Card.Title className="mb-0">
                  Vista Previa - {products.length} productos elegibles
                </Card.Title>
                <small className="text-muted">
                  De un total de {totalMlProducts} productos ML activos en la empresa
                </small>
              </div>
              <div>
                <Button variant="outline-secondary" size="sm" className="mr-2" onClick={handleReset}>
                  ← Atrás
                </Button>
                <Button
                  variant="danger"
                  onClick={handleStart}
                  disabled={!selectedProducts.length}
                >
                  🚀 Iniciar Proceso ({selectedProducts.length} productos)
                </Button>
              </div>
            </div>

            {products.length === 0 ? (
              <Alert variant="info">
                No se encontraron productos que cumplan con los criterios de filtrado.
              </Alert>
            ) : (
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                <Table striped bordered hover size="sm">
                  <thead style={{ position: "sticky", top: 0, background: "white" }}>
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedProducts.length === products.length}
                          onChange={toggleAll}
                        />
                      </th>
                      <th style={{ width: "50px" }}>Img</th>
                      <th>ML ID</th>
                      <th>Título</th>
                      <th>Fecha Publicación</th>
                      <th>Ventas</th>
                      <th>Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => (
                      <tr key={p.mlId || idx}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedProducts.includes(p.mlId)}
                            onChange={() => toggleProduct(p.mlId)}
                          />
                        </td>
                        <td>
                          {p.thumbnail && (
                            <img
                              src={p.thumbnail}
                              alt=""
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                            />
                          )}
                        </td>
                        <td>
                          <a
                            href={p.permalink || `https://mercadolibre.com/items/${p.mlId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {p.mlId}
                          </a>
                        </td>
                        <td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.title}
                        </td>
                        <td>{formatDate(p.startTime)}</td>
                        <td>
                          <Badge variant="secondary">{p.soldQuantity}</Badge>
                        </td>
                        <td>
                          {p.currencyId} {p.price}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* PROCESSING VIEW */}
      {view === "processing" && (
        <Card>
          <Card.Body>
            <Card.Title>Procesando...</Card.Title>

            <ProgressBar
              now={progress}
              label={`${progress.toFixed(1)}%`}
              animated={progress < 100}
              variant={progress >= 100 ? "success" : "primary"}
              className="mb-3"
              style={{ height: "25px" }}
            />

            <div className="text-center">
              <Button variant="danger" onClick={handleCancel}>
                ✋ Cancelar Proceso
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* COMPLETED VIEW */}
      {view === "completed" && (
        <Card>
          <Card.Body>
            <Alert variant="success">
              <Alert.Heading>✅ Proceso Completado</Alert.Heading>
            </Alert>

            {processData?.totalValidated === 0 && (
              <Alert variant="warning">
                <strong>ℹ️ Sin cambios:</strong> Ninguno de los productos seleccionados cumplió los
                criterios al momento de procesar. Es posible que ya hayan sido cerrados, pausados o
                vendidos en ML entre la vista previa y el inicio del proceso.
              </Alert>
            )}

            {processData && (
              <Row className="mb-3">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body className="py-2">
                      <h5 className="mb-0">{processData.total || processData.processed || 0}</h5>
                      <small className="text-muted">Total</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-success">
                    <Card.Body className="py-2">
                      <h5 className="mb-0 text-success">{processData.success || 0}</h5>
                      <small className="text-muted">Exitosos</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-danger">
                    <Card.Body className="py-2">
                      <h5 className="mb-0 text-danger">{processData.failed || 0}</h5>
                      <small className="text-muted">Fallidos</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-warning">
                    <Card.Body className="py-2">
                      <h5 className="mb-0 text-warning">{processData.skipped || 0}</h5>
                      <small className="text-muted">Omitidos</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Product details table */}
            {processData?.products?.length > 0 && (
              <div style={{ maxHeight: "300px", overflowY: "auto" }} className="mb-3">
                <Table striped bordered hover size="sm">
                  <thead style={{ position: "sticky", top: 0, background: "white" }}>
                    <tr>
                      <th>ML ID</th>
                      <th>Título</th>
                      <th>Resultado</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processData.products.map((p, idx) => (
                      <tr key={idx}>
                        <td style={{ fontSize: "0.85rem" }}>{p.mlId}</td>
                        <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.title}
                        </td>
                        <td>
                          <Badge
                            variant={
                              p.result === "success" ? "success" :
                              p.result === "skipped" ? "warning" : "danger"
                            }
                          >
                            {p.result}
                          </Badge>
                        </td>
                        <td style={{ fontSize: "0.8rem" }}>
                          {p.reason || p.error || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={handleReset}>
                Volver al inicio
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ERROR VIEW */}
      {view === "error" && (
        <Card>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>❌ Error</Alert.Heading>
              <p>{errorMsg || "Ocurrió un error inesperado"}</p>
            </Alert>
            <Button variant="outline-secondary" onClick={handleReset}>
              Volver al inicio
            </Button>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
